"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.contourCutTime = contourCutTime;
exports.createSpeedPoints = createSpeedPoints;
exports.getMaxEntitySpeeds = getMaxEntitySpeeds;
exports.integrateSpeedPoints = integrateSpeedPoints;
var _ = require(".");
var _getPointTangent = require("./getPointTangent");
const optionDefaults = {
  cornerAccuracy: 0.001,
  exactStopCheck: false,
  exactStopCheckMinAngle: 0,
  cornerDwell: false,
  cornerDwellTime: 0,
  cornerDwellMinAngle: 0
};
function contourCutTime(contour, maxSpeed, maxAcceleration, options = optionDefaults) {
  var _options$cornerAccura, _options$exactStopChe, _options$exactStopChe2, _options$cornerDwell, _options$cornerDwellT, _options$cornerDwellM;
  (_options$cornerAccura = options.cornerAccuracy) !== null && _options$cornerAccura !== void 0 ? _options$cornerAccura : options.cornerAccuracy = optionDefaults.cornerAccuracy;
  (_options$exactStopChe = options.exactStopCheck) !== null && _options$exactStopChe !== void 0 ? _options$exactStopChe : options.exactStopCheck = optionDefaults.exactStopCheck;
  (_options$exactStopChe2 = options.exactStopCheckMinAngle) !== null && _options$exactStopChe2 !== void 0 ? _options$exactStopChe2 : options.exactStopCheckMinAngle = optionDefaults.exactStopCheckMinAngle;
  (_options$cornerDwell = options.cornerDwell) !== null && _options$cornerDwell !== void 0 ? _options$cornerDwell : options.cornerDwell = optionDefaults.cornerDwell;
  (_options$cornerDwellT = options.cornerDwellTime) !== null && _options$cornerDwellT !== void 0 ? _options$cornerDwellT : options.cornerDwellTime = optionDefaults.cornerDwellTime;
  (_options$cornerDwellM = options.cornerDwellMinAngle) !== null && _options$cornerDwellM !== void 0 ? _options$cornerDwellM : options.cornerDwellMinAngle = optionDefaults.cornerDwellMinAngle;
  let entities = getMaxEntitySpeeds(contour, maxSpeed, maxAcceleration, options);
  // console.log(entities)

  let speedPoints = createSpeedPoints(entities, maxAcceleration);
  // console.log(speedPoints)

  let cutTime = integrateSpeedPoints(speedPoints, maxAcceleration);
  let dwellTime = entities.filter(e => e.cornerDwellAtExit).length * options.cornerDwellTime;
  // console.log(cutTime, dwellTime)
  return cutTime + dwellTime;
}
function getMaxEntitySpeeds(contour, maxSpeed, maxAcceleration, options = optionDefaults) {
  // Determine max speed for each entity
  let entities = contour.entities.map(e => {
    let maxEntitySpeed;
    switch (e.type) {
      case 'LINE':
        maxEntitySpeed = maxSpeed;
        break;
      case 'ARC':
      case 'CIRCLE':
        maxEntitySpeed = Math.min(maxSpeed, Math.sqrt(maxAcceleration * e.r));
        break;
    }
    return {
      entity: e,
      maxSpeed: maxEntitySpeed
    };
  });

  // Determine max entry and exit speeds based on angle between adjacent entities
  entities[0].entrySpeed = 0;
  entities[entities.length - 1].exitSpeed = 0;
  for (let i = 0; i < entities.length - 1; i++) {
    const e1 = entities[i];
    const e2 = entities[i + 1];
    let angle = (0, _getPointTangent.getAngleBetweenEntities)(e1.entity, e2.entity);

    // Get angle to next entity
    if (options.exactStopCheck && Math.abs(angle) > Math.PI - options.exactStopCheckMinAngle) {
      // Use exact stop check
      e1.exitSpeed = 0;
      e2.entrySpeed = 0;
    } else if (options.cornerDwell && Math.abs(angle) > Math.PI - options.cornerDwellMinAngle) {
      // TODO: Corner dwell is only used on convex corners, but we're using it on both convex and non-convex. See
      // NCGenerator for determining the difference.
      // Use corner dwell
      e1.exitSpeed = 0;
      e2.entrySpeed = 0;
      e1.cornerDwellAtExit = true;
    } else {
      if (Math.abs(angle) < 1e-6) {
        // No change in direction, so additional restriction on speed
        e1.exitSpeed = Math.min(maxSpeed, e1.maxSpeed, e2.maxSpeed);
        e2.entrySpeed = Math.min(maxSpeed, e1.maxSpeed, e2.maxSpeed);
      } else if (Math.abs(angle) > Math.PI - 1e-6) {
        // Reverse direction
        e1.exitSpeed = 0;
        e2.entrySpeed = 0;
      } else {
        // Assume the laser rounds the corner such that the error is no greater than the corner accuracy -- what radius
        // of curvature would be needed to do that?
        let approximatingRadiusOfCurvature = Math.max(options.cornerAccuracy, 0) / (1 / Math.sin(Math.abs(angle) / 2) - 1);
        let topCornerSpeed = Math.sqrt(maxAcceleration * approximatingRadiusOfCurvature);
        if (approximatingRadiusOfCurvature < 0) {
          console.log({
            approximatingRadiusOfCurvature
          });
          throw new Error('approximatingRadiusOfCurvature < 0');
        }
        // console.log({angle})
        // console.log(approximatingRadiusOfCurvature)
        // console.log(topCornerSpeed)
        e1.exitSpeed = Math.min(maxSpeed, e1.maxSpeed, e2.maxSpeed, topCornerSpeed);
        e2.entrySpeed = Math.min(maxSpeed, e1.maxSpeed, e2.maxSpeed, topCornerSpeed);
      }
    }
  }

  // Determine length and position of each entity
  let position = 0;
  for (let i = 0; i < entities.length; i++) {
    const e = entities[i];
    e.position = position;
    e.length = (0, _.entityLength)(e.entity);
    position += e.length;
  }
  return entities;
}
function createSpeedPoints(entities, maxAcceleration) {
  // Plot the square of the entry and exit speeds on a graph of speed^2 vs. distance.
  let speedPoints = entities.map(e => ({
    x: e.position,
    y: e.entrySpeed ** 2,
    maxSpeedToTheRight: e.maxSpeed
  }));
  speedPoints.push({
    x: entities[entities.length - 1].position + entities[entities.length - 1].length,
    y: entities[entities.length - 1].exitSpeed ** 2
  });
  // TODO: Remove speed points that are above the speed curve, or in other words, that are too fast to be reached while
  // traveling from adjacent entities

  // Begin on the left-most point (which is at the origin, representing zero entry speed for the first entity), and draw
  // a positive sloping line upward from the first point. This represents the speed^2 as we accelerate away from that
  // point at a constant acceleration. 
  let slope = 2 * maxAcceleration;
  let minimumIntercept = speedPoints[0].y - slope * speedPoints[0].x;
  for (let i = 1; i < speedPoints.length - 1; i++) {
    let intercept = speedPoints[i].y - slope * speedPoints[i].x;
    if (intercept < minimumIntercept) {
      minimumIntercept = intercept;
    } else {
      // Set the previous point's max speed to this one's
      speedPoints[i - 1].maxSpeedToTheRight = speedPoints[i].maxSpeedToTheRight;

      // Remove this point
      speedPoints.splice(i, 1);
      i--;
    }
  }

  // Now go in the reverse direction

  slope = -2 * maxAcceleration;
  minimumIntercept = speedPoints[speedPoints.length - 1].y - slope * speedPoints[speedPoints.length - 1].x;
  for (let i = speedPoints.length - 2; i > 0; i--) {
    let intercept = speedPoints[i].y - slope * speedPoints[i].x;
    if (intercept < minimumIntercept) {
      minimumIntercept = intercept;
    } else {
      // The next point's max speed will stay the same

      // Remove this point
      speedPoints.splice(i, 1);
    }
  }
  return speedPoints;
}
function integrateSpeedPoints(speedPoints, maxAcceleration) {
  let totalTime = 0;
  for (let i = 0; i < speedPoints.length - 1; i++) {
    let entrySpeed = Math.sqrt(speedPoints[i].y);
    let exitSpeed = Math.sqrt(speedPoints[i + 1].y);
    let maxSpeed = speedPoints[i].maxSpeedToTheRight;
    if (maxSpeed == null) {
      throw new Error(`maxSpeed==null`);
    }
    let distance = speedPoints[i + 1].x - speedPoints[i].x;
    let accelerationTime = (maxSpeed - entrySpeed) / maxAcceleration;
    let decelerationTime = (maxSpeed - exitSpeed) / maxAcceleration;
    let accelerationDistance = entrySpeed * accelerationTime + 0.5 * maxAcceleration * accelerationTime * accelerationTime;
    let decelerationDistance = exitSpeed * decelerationTime + 0.5 * maxAcceleration * decelerationTime * decelerationTime;
    let cruisingTime;
    if (accelerationDistance + decelerationDistance > distance) {
      // Not enough distance to reach max speed
      // Determine the point we will reach maximum speed before we have to begin decelerating
      let topSpeedPoint = -((entrySpeed ** 2 - exitSpeed ** 2) / (2 * maxAcceleration) - distance) * 0.5;
      accelerationTime = (-entrySpeed + Math.sqrt(entrySpeed * entrySpeed + 2 * maxAcceleration * topSpeedPoint)) / maxAcceleration;
      decelerationTime = (-exitSpeed + Math.sqrt(exitSpeed * exitSpeed + 2 * maxAcceleration * (distance - topSpeedPoint))) / maxAcceleration;
      accelerationDistance = topSpeedPoint;
      decelerationDistance = distance - topSpeedPoint;
      cruisingTime = 0;
    } else {
      cruisingTime = (distance - accelerationDistance - decelerationDistance) / maxSpeed;
    }
    // console.log({
    //   entrySpeed,
    //   exitSpeed,
    //   maxSpeed,
    //   distance,
    //   accelerationTime,
    //   decelerationTime,
    //   accelerationDistance,
    //   decelerationDistance,
    //   cruisingTime
    // })
    totalTime += accelerationTime + cruisingTime + decelerationTime;
  }
  return totalTime;
}